import { ChevronRight, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type ReactNode, type CSSProperties } from "react";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string; style?: CSSProperties }>;
  color?: string;
}

interface ReportPageWrapperProps {
  title: string;
  breadcrumbItems: BreadcrumbItem[];
  action?: ReactNode;
  children: ReactNode;
}

export function StatCard({ label, value, icon: Icon, color = "#009A44" }: StatCardProps) {
  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl">
      <CardContent className="p-4 flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: color + "15" }}
        >
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 truncate">{label}</p>
          <p className="text-base sm:text-lg font-bold text-slate-900 mt-0.5 truncate">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportPageWrapper({ title, breadcrumbItems, action, children }: ReportPageWrapperProps) {
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-500">
        {breadcrumbItems.map((item, idx) => (
          <span key={idx} className="flex items-center gap-1.5">
            {idx > 0 && <ChevronRight className="h-3 w-3" />}
            {item.path ? (
              <Link to={item.path} className="hover:text-[#009A44] transition-colors">
                {item.label}
              </Link>
            ) : (
              <span className="text-slate-700 font-medium">{item.label}</span>
            )}
          </span>
        ))}
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {action || (
          <Button className="bg-[#009A44] hover:bg-[#00803A] text-white gap-2 shadow-sm">
            <BarChart3 className="h-4 w-4" />
            Generate Report
          </Button>
        )}
      </div>

      {/* Content */}
      {children}
    </div>
  );
}
